import {useSendTherapySequenceEvent} from '@/modules/patients-therapies/api'
import {useTherapySequenceActor} from '@/modules/patients-therapies/hooks'
import {openPickingListDrawer} from '@/modules/production/components/picking-list/drawer/picking-list-drawer-util'
import {useGetSettings} from '@/modules/settings/api'
import {AdministrationMode, PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {
  PatientTherapySequenceEvent,
  PatientTherapySequenceEventType,
  SendPatientTherapySequenceEventSchema,
} from '@cypro/engine/mutators'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {isBlockedByPastTherapyDate, isUseByGuardedEvent} from '@cypro/engine/utils'
import {toaster} from '@cypro/ui/toaster'
import {getActorNextEvents} from '@cypro/ui/utils'
import {useQueryClient} from '@tanstack/react-query'
import {useNavigate} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {useCallback, useMemo} from 'react'
import {openAdministerDrawer} from '../components/administer-drawer/util'
import {openManualAdministerDialog} from '../components/manual-administer-dialog/util'
import {openPatientTherapySequenceDrawerById} from '../components/patient-therapy-sequence-drawer/util'
import {openReturnToPharmacyDialog} from '../components/return-to-pharmacy-dialog/util'
import {openReversePatientArrivedDialog} from '../components/reverse-patient-arrived-dialog/util'
import {openRevisePrescriptionDialog} from '../components/revise-prescription-dialog/util'
import {openCancelProductionDialog} from '../components/cancel-production-dialog/util'
import {openSpoilageDialog} from '../components/spoilage-dialog/util'
import {useFormatTherapySequenceLabel} from './use-format-therapy-sequence-label'
import {useCheckPreparationExpired} from './use-preparation-use-by'

const statuses = Object.values(PatientTherapySequenceStatus.enum)
const eventTypes = Object.values(PatientTherapySequenceEventType.enum)

interface StatusLabel {
  status: PatientTherapySequenceStatus | 'validating.pharmacistCheck' | 'validating.patientArrival'
  title: string
  description?: string
}

export type Action = ReturnType<typeof useTherapySequenceActions>['actions'][number]

// permission check needs a body since PHARMACIST_VALIDATE carries one;
// an empty medicine list is enough for the guard to validate the event shape
export function canPharmacistValidate(actor: ReturnType<typeof useTherapySequenceActor>[0]) {
  return actor.can({
    type: PatientTherapySequenceEventType.enum.PHARMACIST_VALIDATE,
    body: {medicine: []},
  })
}

export function canDoctorValidate(actor: ReturnType<typeof useTherapySequenceActor>[0]) {
  return actor.can({type: 'DOCTOR_VALIDATE'})
}

// pending_approval: only board members (ADMIN) may approve or reject the budget
export function canApproveBudget(actor: ReturnType<typeof useTherapySequenceActor>[0]) {
  return actor.can({type: 'APPROVE_BUDGET'})
}

export function canRejectBudget(actor: ReturnType<typeof useTherapySequenceActor>[0]) {
  return actor.can({type: 'REJECT_BUDGET'})
}

export function useTherapySequenceActions(sequence: PatientTherapySequence) {
  const [actor, sendToActor] = useTherapySequenceActor(sequence)
  const isSmartCompounder = Boolean(sequence.compoundedByMachine)
  const formatLabel = useFormatTherapySequenceLabel(sequence)
  const {t} = useTranslate('common')
  const {settings} = useGetSettings()
  const isManualAdministration = settings?.administrationMode === AdministrationMode.enum.MANUAL

  const sendToServer = useSendTherapySequenceEvent()
  const checkPreparationExpired = useCheckPreparationExpired()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {actions, statusLabels} = useMemo(() => {
    const nextEvents = getActorNextEvents(actor)
    const actions = eventTypes.filter(type => {
      const event = {type} as PatientTherapySequenceEvent
      return actor.can(event) && nextEvents.has(type)
    })

    const statusLabels = statuses
      .map(status => {
        if (status === PatientTherapySequenceStatus.enum.validating || !actor.matches(status)) return null
        return {
          status,
          title: formatLabel(status, 'title'),
          description: formatLabel(status, 'description'),
        }
      })
      .filter(Boolean) as StatusLabel[]

    if (actor.matches(PatientTherapySequenceStatus.enum.validating)) {
      if (actor.matches({validating: {pharmacistCheck: 'pending'}})) {
        statusLabels.push({
          status: 'validating.pharmacistCheck',
          title: formatLabel('validating.pharmacistCheck', 'title'),
          description: formatLabel('validating.pharmacistCheck', 'description'),
        })
      }
      if (actor.matches({validating: {patientArrival: 'pending'}})) {
        statusLabels.push({
          status: 'validating.patientArrival',
          title: formatLabel('validating.patientArrival', 'title'),
          description: formatLabel('validating.patientArrival', 'description'),
        })
      }
    }

    return {actions, statusLabels}
  }, [formatLabel, actor])

  const sendEvent = useCallback(
    async (payload: SendPatientTherapySequenceEventSchema) => {
      if (!actor.can(payload.event)) throw new Error(`Cannot perform event ${payload.event.type}`)

      // an expired bag is never released or administered (CY-327); the server refuses it too
      if (isUseByGuardedEvent(payload.event.type) && (await checkPreparationExpired(sequence.id))) {
        toaster.error({
          title: t('preparation_expired_blocked', 'Preparation expired: it cannot be released or administered'),
        })
        return
      }

      if (payload.event.type === 'PHARMACIST_VALIDATE' && !payload.event.body) {
        return openPatientTherapySequenceDrawerById(sequence.id, {openPrescribedName: true})
      }
      if (payload.event.type === 'ADMINISTER' && !payload?.event?.body?.completed) {
        // SCAN mode verifies patient + label barcodes in the drawer; MANUAL asks for a checked confirmation
        if (!isManualAdministration) return openAdministerDrawer(sequence)

        // the drawer blocks past-dated sequences; keep that guard in Manual mode too
        if (isBlockedByPastTherapyDate(sequence)) {
          toaster.error({title: t('therapy_sequence_disabled_because_is_passed')})
          return
        }
        return openManualAdministerDialog(sequence)
      }

      if (payload.event.type === 'RETURN_TO_PHARMACY' && !('body' in payload.event)) {
        return openReturnToPharmacyDialog(sequence)
      }

      if (payload.event.type === 'SPOILAGE' && !('body' in payload.event)) {
        return openSpoilageDialog(sequence)
      }

      // Reverse opens a confirmation dialog; the dialog re-sends the event with an (empty) body.
      if (payload.event.type === 'REVERSE_PATIENT_ARRIVED' && !payload.event.body) {
        return openReversePatientArrivedDialog(sequence)
      }

      // Cancel production reuses the cleanroom abort dialog (material report + reason).
      if (payload.event.type === 'CANCEL_PRODUCTION') {
        return openCancelProductionDialog(sequence)
      }

      // Revise opens the prescription editor prefilled; submitting updates the sequence.
      if (payload.event.type === 'REVISE') {
        return openRevisePrescriptionDialog(sequence)
      }

      if (payload.event.type === 'COMPLETE_PRODUCTION') {
        if (isSmartCompounder) {
          navigate({to: '/configuration/smart-compounders/orders'})
        } else {
          navigate({
            to: '/cleanroom',
            search: {sequenceId: sequence.id},
          })
        }
        return
      }

      if (payload.event.type === 'START_PRODUCTION') {
        openPickingListDrawer(sequence.id, {selectedOrders: [sequence]})
        return
      }

      const res = await sendToServer(payload)
      // attemptMutation already showed the server's refusal; tell the caller so no success message follows
      if (!res) return {type: 'error' as const}
      if (res.type === 'error') return res

      sendToActor(payload.event)
      queryClient.invalidateQueries({queryKey: ['current-user-actions-production-therapies-count']})

      return res
    },
    [
      sendToServer,
      actor,
      sendToActor,
      sequence,
      queryClient,
      navigate,
      isSmartCompounder,
      isManualAdministration,
      t,
      checkPreparationExpired,
    ]
  )

  return {actions, actor, sendEvent, statusLabels}
}
